import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';

import { Subscription } from '../../types';
import './utils/compatibility-fix';

interface TeamSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  availableSubscriptions: Subscription[];
  currentSubscription: Subscription | null;
  orgId: string;
  onChangeSubscription: (subscriptionId: string) => void;
}

const TeamSubscriptionDialog: React.FC<TeamSubscriptionDialogProps> = ({
  open,
  onClose,
  availableSubscriptions,
  currentSubscription,
  orgId,
  onChangeSubscription
}) => {
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>(
    currentSubscription?._id || ''
  );

  const handleChangeSubscription = () => {
    onChangeSubscription(selectedSubscriptionId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Change Team Subscription</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          Select a subscription to assign to this team. The team will share resources with other teams on the same subscription.
        </DialogContentText>
        
        {availableSubscriptions.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <Typography variant="body1" color="error" gutterBottom>
              No subscriptions available for this organization.
            </Typography>
            <Button 
              variant="contained" 
              component={Link} 
              to={`/organizations/${orgId}?tab=3`}
              sx={{ mt: 1 }}
            >
              Add Subscription
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {availableSubscriptions.map(subscription => {
              const isCurrentSubscription = currentSubscription && currentSubscription._id === subscription._id;
              const teamsLimit = subscription.limits?.maxTeams || subscription.limits?.teamsPerOrg || 0;
              const currentTeamsCount = subscription.currentUsage?.teamsUsed || subscription.currentUsage?.teamsCount || subscription.usage?.teamsCount || subscription.usage?.teamsUsed || 0;
              
              const isAtCapacity = 
                teamsLimit !== 9999 && currentTeamsCount >= teamsLimit;
              
              // Don't disable current subscription even if at capacity because team is already using it
              const isDisabled = isAtCapacity && !isCurrentSubscription;
              
              return (
                <Grid item xs={12} key={subscription._id}>
                  <Card 
                    variant={isCurrentSubscription ? "elevation" : "outlined"}
                    elevation={isCurrentSubscription ? 4 : 0}
                    sx={{ 
                      mb: 1, 
                      position: 'relative',
                      opacity: isDisabled ? 0.7 : 1,
                      borderColor: selectedSubscriptionId === subscription._id ? 'primary.main' : 'divider',
                      borderWidth: selectedSubscriptionId === subscription._id ? 2 : 1,
                      cursor: isDisabled ? 'default' : 'pointer'
                    }}
                    onClick={() => !isDisabled && setSelectedSubscriptionId(subscription._id)}
                  >
                    {isCurrentSubscription && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          right: 0, 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          px: 2,
                          py: 0.5,
                          borderBottomLeftRadius: 8
                        }}
                      >
                        Current
                      </Box>
                    )}
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="h6" gutterBottom>
                            {subscription.referenceName || subscription.name || `${subscription.planId} Subscription`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {(subscription.plan || subscription.planId || 'unknown').charAt(0).toUpperCase() + (subscription.plan || subscription.planId || 'unknown').slice(1)} Plan
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Status: {(typeof subscription.status === 'string' ? subscription.status : 'active').toUpperCase()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Renews: {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle2" gutterBottom>
                            Resource Limits
                          </Typography>
                          <Typography variant="body2">
                            • Teams: {teamsLimit === 9999 || teamsLimit === -1 ? 'Unlimited' : teamsLimit}
                          </Typography>
                          <Typography variant="body2">
                            • Users: {subscription.limits?.maxConcurrentUsers || subscription.limits?.usersPerOrg || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            • Storage: {subscription.limits?.maxStorageBytes ? Math.round(subscription.limits.maxStorageBytes / (1024 * 1024 * 1024)) : subscription.limits?.storage || 'N/A'} GB
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle2" gutterBottom>
                            Current Usage
                          </Typography>
                          <Typography variant="body2">
                            • Teams: {currentTeamsCount}/
                            {teamsLimit === 9999 || teamsLimit === -1 ? '∞' : teamsLimit}
                          </Typography>
                          <Typography variant="body2">
                            • Users: {subscription.currentUsage?.currentConcurrentUsers || subscription.currentUsage?.activeUsers || subscription.usage?.activeUsers || subscription.usage?.currentConcurrentUsers || 0}/{subscription.limits?.maxConcurrentUsers || subscription.limits?.usersPerOrg || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            • Storage: {subscription.currentUsage?.storageUsedBytes ? Math.round(subscription.currentUsage.storageUsedBytes / (1024 * 1024 * 1024)) : subscription.currentUsage?.storageUsed || subscription.usage?.storageUsed || 0}/{subscription.limits?.maxStorageBytes ? Math.round(subscription.limits.maxStorageBytes / (1024 * 1024 * 1024)) : subscription.limits?.storage || 'N/A'} GB
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      {isDisabled && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                          This subscription has reached its maximum team capacity.
                        </Typography>
                      )}
                      
                      {(subscription.teams?.length || 0) > 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Currently assigned to {subscription.teams?.length || 0} team(s)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          component={Link} 
          to={`/organizations/${orgId}?tab=3`}
          color="primary"
        >
          Manage Subscriptions
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleChangeSubscription} 
          variant="contained" 
          disabled={
            !selectedSubscriptionId || 
            (currentSubscription && currentSubscription._id === selectedSubscriptionId) ||
            availableSubscriptions.length === 0
          }
        >
          Assign Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamSubscriptionDialog;